import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { getPnL, getBalanceSheet, exportPnLPdf, exportBalanceSheetPdf, exportTrialBalancePdf, getTrialBalance } from '../services/api';
import DateRangePicker from '../components/DateRangePicker';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Download, TrendingUp, TrendingDown, Equal } from 'lucide-react';

export default function FinancialStatementsPage() {
  const [pnl, setPnl] = useState(null);
  const [bs, setBs] = useState(null);
  const [tb, setTb] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [activeTab, setActiveTab] = useState('pnl');

  useEffect(() => { fetchData(); }, [dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const [pnlRes, bsRes, tbRes] = await Promise.all([getPnL(params), getBalanceSheet({ as_of: dateTo }), getTrialBalance({ as_of: dateTo })]);
      setPnl(pnlRes.data);
      setBs(bsRes.data);
      setTb(tbRes.data);
    } catch (error) {
      toast.error('Failed to load financial statements');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPdf = async () => {
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      
      let response, filename;
      if (activeTab === 'pnl') {
        response = await exportPnLPdf(params);
        filename = 'pnl_report.pdf';
      } else if (activeTab === 'bs') {
        response = await exportBalanceSheetPdf({ as_of: dateTo });
        filename = 'balance_sheet.pdf';
      } else {
        response = await exportTrialBalancePdf({ as_of: dateTo });
        filename = 'trial_balance.pdf';
      }
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (e) { toast.error('Failed to export PDF'); }
  };

  if (loading) {
    return (<Layout title="Financial Statements"><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#D98A4A', borderTopColor: 'transparent' }}></div></div></Layout>);
  }

  return (
    <Layout title="Financial Statements">
      <div data-testid="financial-statements-page" className="space-y-6">
        <div className="flex items-center justify-between">
          <DateRangePicker dateFrom={dateFrom} dateTo={dateTo} onChange={({ dateFrom: df, dateTo: dt }) => { setDateFrom(df); setDateTo(dt); }} />
          <Button onClick={handleExportPdf} className="rounded-full font-body font-medium text-white px-6 transition-colors duration-200" style={{ background: '#C84B31' }} data-testid="export-pdf-btn">
            <Download size={16} className="mr-2" /> Export PDF
          </Button>
        </div>

        <Tabs defaultValue="pnl" onValueChange={setActiveTab}>
          <TabsList className="p-1 rounded-xl" style={{ background: '#E5E3DB' }}>
            <TabsTrigger value="pnl" className="rounded-lg font-body data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-pnl">Profit & Loss</TabsTrigger>
            <TabsTrigger value="bs" className="rounded-lg font-body data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-bs">Balance Sheet</TabsTrigger>
            <TabsTrigger value="tb" className="rounded-lg font-body data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-tb">Trial Balance</TabsTrigger>
          </TabsList>

          {/* P&L */}
          <TabsContent value="pnl" className="mt-6">
            {pnl && (
              <div className="bg-white rounded-xl overflow-hidden" style={{ border: '1px solid #E5E3DB' }}>
                <div className="px-6 py-4" style={{ borderBottom: '1px solid #E5E3DB', background: 'rgba(198,156,109,0.04)' }}>
                  <h3 className="font-heading text-xl font-medium">Profit & Loss</h3>
                  <p className="text-xs font-body" style={{ color: '#9CA3AF' }}>{dateFrom || 'All'} to {dateTo || 'Present'}</p>
                </div>

                <div className="px-6 py-5" style={{ borderBottom: '1px solid #E5E3DB' }}>
                  <h4 className="text-[10px] font-body uppercase tracking-[0.2em] font-semibold mb-3 flex items-center gap-2" style={{ color: '#2D7A5D' }}>
                    <TrendingUp size={14} /> Revenue
                  </h4>
                  {pnl.revenue.items.length > 0 ? pnl.revenue.items.map((item) => (
                    <div key={item.code} className="flex items-center justify-between py-1.5 text-sm font-body">
                      <span style={{ color: '#4B5563' }}><span className="font-mono mr-2" style={{ color: '#9CA3AF' }}>{item.code}</span>{item.account_name}</span>
                      <span className="font-mono font-medium" style={{ color: '#2D7A5D' }}>${item.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                  )) : <p className="text-sm font-body" style={{ color: '#9CA3AF' }}>No revenue transactions</p>}
                  <div className="flex items-center justify-between pt-3 mt-3 font-medium font-body" style={{ borderTop: '2px solid #1A1A1A' }}>
                    <span>Total Revenue</span>
                    <span className="font-mono" style={{ color: '#2D7A5D' }}>${pnl.revenue.total.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>

                <div className="px-6 py-5" style={{ borderBottom: '1px solid #E5E3DB' }}>
                  <h4 className="text-[10px] font-body uppercase tracking-[0.2em] font-semibold mb-3 flex items-center gap-2" style={{ color: '#C84B31' }}>
                    <TrendingDown size={14} /> Expenses
                  </h4>
                  {pnl.expenses.items.length > 0 ? pnl.expenses.items.map((item) => (
                    <div key={item.code} className="flex items-center justify-between py-1.5 text-sm font-body">
                      <span style={{ color: '#4B5563' }}><span className="font-mono mr-2" style={{ color: '#9CA3AF' }}>{item.code}</span>{item.account_name}</span>
                      <span className="font-mono font-medium" style={{ color: '#C84B31' }}>${item.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                  )) : <p className="text-sm font-body" style={{ color: '#9CA3AF' }}>No expense transactions</p>}
                  <div className="flex items-center justify-between pt-3 mt-3 font-medium font-body" style={{ borderTop: '2px solid #1A1A1A' }}>
                    <span>Total Expenses</span>
                    <span className="font-mono" style={{ color: '#C84B31' }}>${pnl.expenses.total.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>

                <div className="px-6 py-5" style={{ background: 'rgba(198,156,109,0.04)' }}>
                  <div className="flex items-center justify-between text-xl font-heading font-medium">
                    <span>Net Income</span>
                    <span className="font-mono" style={{ color: pnl.net_income >= 0 ? '#2D7A5D' : '#C84B31' }}>
                      ${pnl.net_income.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Balance Sheet */}
          <TabsContent value="bs" className="mt-6">
            {bs && (
              <div className="bg-white rounded-xl overflow-hidden" style={{ border: '1px solid #E5E3DB' }}>
                <div className="px-6 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid #E5E3DB', background: 'rgba(198,156,109,0.04)' }}>
                  <div>
                    <h3 className="font-heading text-xl font-medium">Balance Sheet</h3>
                    <p className="text-xs font-body" style={{ color: '#9CA3AF' }}>As of {bs.as_of}</p>
                  </div>
                  {bs.balanced ? (
                    <span className="flex items-center gap-1 text-xs font-body font-semibold" style={{ color: '#2D7A5D' }}><Equal size={12} /> Balanced</span>
                  ) : (
                    <span className="text-xs font-body font-semibold" style={{ color: '#C84B31' }}>Unbalanced</span>
                  )}
                </div>

                {[{ title: 'Assets', items: bs.assets, total: bs.assets.total, color: '#D98A4A' },
                  { title: 'Liabilities', items: bs.liabilities, total: bs.liabilities.total, color: '#D98A4A' },
                  { title: 'Equity', items: bs.equity, total: bs.equity.total, color: '#C84B31' }
                ].map((section) => (
                  <div key={section.title} className="px-6 py-5" style={{ borderBottom: '1px solid #E5E3DB' }}>
                    <h4 className="text-[10px] font-body uppercase tracking-[0.2em] font-semibold mb-3" style={{ color: section.color }}>{section.title}</h4>
                    {section.items.items.length > 0 ? section.items.items.map((item) => (
                      <div key={item.code} className="flex items-center justify-between py-1.5 text-sm font-body">
                        <span style={{ color: '#4B5563' }}><span className="font-mono mr-2" style={{ color: '#9CA3AF' }}>{item.code}</span>{item.account_name}</span>
                        <span className="font-mono font-medium" style={{ color: section.color }}>${item.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                      </div>
                    )) : <p className="text-sm font-body" style={{ color: '#9CA3AF' }}>No {section.title.toLowerCase()}</p>}
                    <div className="flex items-center justify-between pt-3 mt-3 font-medium font-body" style={{ borderTop: '2px solid #1A1A1A' }}>
                      <span>Total {section.title}</span>
                      <span className="font-mono" style={{ color: section.color }}>${section.total.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Trial Balance */}
          <TabsContent value="tb" className="mt-6">
            {tb && (
              <div className="bg-white rounded-xl overflow-hidden" style={{ border: '1px solid #E5E3DB' }}>
                <div className="px-6 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid #E5E3DB', background: 'rgba(198,156,109,0.04)' }}>
                  <div>
                    <h3 className="font-heading text-xl font-medium">Trial Balance</h3>
                    <p className="text-xs font-body" style={{ color: '#9CA3AF' }}>As of {tb.as_of}</p>
                  </div>
                  {tb.balanced ? (
                    <span className="flex items-center gap-1 text-xs font-body font-semibold" style={{ color: '#2D7A5D' }}><Equal size={12} /> Balanced</span>
                  ) : (
                    <span className="text-xs font-body font-semibold" style={{ color: '#C84B31' }}>Unbalanced</span>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left" style={{ borderBottom: '1px solid #E5E3DB' }}>
                        <th className="px-4 py-3 text-[10px] font-body uppercase tracking-[0.15em] font-semibold" style={{ color: '#9CA3AF' }} rowSpan={2}>Account</th>
                        <th className="px-4 py-3 text-center text-[10px] font-body uppercase tracking-[0.15em] font-semibold" style={{ color: '#9CA3AF', borderLeft: '1px solid #E5E3DB' }} colSpan={2}>Pre-Adjustment</th>
                        <th className="px-4 py-3 text-center text-[10px] font-body uppercase tracking-[0.15em] font-semibold" style={{ color: '#9CA3AF', borderLeft: '1px solid #E5E3DB' }} colSpan={2}>Adjustments</th>
                        <th className="px-4 py-3 text-center text-[10px] font-body uppercase tracking-[0.15em] font-semibold" style={{ color: '#9CA3AF', borderLeft: '1px solid #E5E3DB' }} colSpan={2}>Post-Adjustment</th>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #E5E3DB' }}>
                        <th className="px-4 py-2 text-right text-[10px] font-body" style={{ color: '#9CA3AF', borderLeft: '1px solid #E5E3DB' }}>Debit</th>
                        <th className="px-4 py-2 text-right text-[10px] font-body" style={{ color: '#9CA3AF' }}>Credit</th>
                        <th className="px-4 py-2 text-right text-[10px] font-body" style={{ color: '#9CA3AF', borderLeft: '1px solid #E5E3DB' }}>Debit</th>
                        <th className="px-4 py-2 text-right text-[10px] font-body" style={{ color: '#9CA3AF' }}>Credit</th>
                        <th className="px-4 py-2 text-right text-[10px] font-body" style={{ color: '#9CA3AF', borderLeft: '1px solid #E5E3DB' }}>Debit</th>
                        <th className="px-4 py-2 text-right text-[10px] font-body" style={{ color: '#9CA3AF' }}>Credit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tb.rows.map((row) => (
                        <tr key={row.code} className="text-sm font-body transition-colors duration-200" style={{ borderBottom: '1px solid #E5E3DB' }}>
                          <td className="px-4 py-2"><span className="font-mono mr-2" style={{ color: '#9CA3AF' }}>{row.code}</span>{row.name}</td>
                          <td className="px-4 py-2 text-right font-mono" style={{ borderLeft: '1px solid #E5E3DB' }}>{row.pre_debit > 0 ? `$${row.pre_debit.toLocaleString('en-US', {minimumFractionDigits: 2})}` : ''}</td>
                          <td className="px-4 py-2 text-right font-mono">{row.pre_credit > 0 ? `$${row.pre_credit.toLocaleString('en-US', {minimumFractionDigits: 2})}` : ''}</td>
                          <td className="px-4 py-2 text-right font-mono" style={{ borderLeft: '1px solid #E5E3DB' }}>{row.adj_debit > 0 ? `$${row.adj_debit.toLocaleString('en-US', {minimumFractionDigits: 2})}` : ''}</td>
                          <td className="px-4 py-2 text-right font-mono">{row.adj_credit > 0 ? `$${row.adj_credit.toLocaleString('en-US', {minimumFractionDigits: 2})}` : ''}</td>
                          <td className="px-4 py-2 text-right font-mono font-medium" style={{ borderLeft: '1px solid #E5E3DB' }}>{row.post_debit > 0 ? `$${row.post_debit.toLocaleString('en-US', {minimumFractionDigits: 2})}` : ''}</td>
                          <td className="px-4 py-2 text-right font-mono font-medium">{row.post_credit > 0 ? `$${row.post_credit.toLocaleString('en-US', {minimumFractionDigits: 2})}` : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="font-bold text-sm font-body" style={{ borderTop: '2px solid #1A1A1A', background: 'rgba(198,156,109,0.04)' }}>
                        <td className="px-4 py-3">Totals</td>
                        <td className="px-4 py-3 text-right font-mono" style={{ borderLeft: '1px solid #E5E3DB' }}>${tb.totals.pre_debit.toLocaleString('en-US', {minimumFractionDigits: 2})}</td>
                        <td className="px-4 py-3 text-right font-mono">${tb.totals.pre_credit.toLocaleString('en-US', {minimumFractionDigits: 2})}</td>
                        <td className="px-4 py-3 text-right font-mono" style={{ borderLeft: '1px solid #E5E3DB' }}>${tb.totals.adj_debit.toLocaleString('en-US', {minimumFractionDigits: 2})}</td>
                        <td className="px-4 py-3 text-right font-mono">${tb.totals.adj_credit.toLocaleString('en-US', {minimumFractionDigits: 2})}</td>
                        <td className="px-4 py-3 text-right font-mono" style={{ borderLeft: '1px solid #E5E3DB' }}>${tb.totals.post_debit.toLocaleString('en-US', {minimumFractionDigits: 2})}</td>
                        <td className="px-4 py-3 text-right font-mono">${tb.totals.post_credit.toLocaleString('en-US', {minimumFractionDigits: 2})}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
